import { RefObject } from 'react';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send } from 'lucide-react';

// ─── Shared render props type ─────────────────────────────────────────────────

export interface ChatInputRenderProps {
  input: string;
  setInput: (v: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isTyping: boolean;
  placeholder: string;
  inputRef: RefObject<HTMLTextAreaElement>;
}

// ─── Default textarea input ───────────────────────────────────────────────────

export const ChatInput = ({
  input,
  setInput,
  handleSubmit,
  isTyping,
  placeholder,
  inputRef,
}: ChatInputRenderProps) => (
  <InputArea component="form" onSubmit={handleSubmit}>
    <TextareaWrap>
      <ChatTextarea
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e as any); } }}
        placeholder={placeholder}
        disabled={isTyping}
        rows={2}
        autoFocus
        $muted={isTyping}
      />
      <SendBtn
        type="submit"
        aria-label="Send message"
        size="small"
        disabled={isTyping || !input.trim()}
      >
        <Send size={16} aria-hidden />
      </SendBtn>
    </TextareaWrap>
  </InputArea>
);

// ─── Styled ───────────────────────────────────────────────────────────────────

const InputArea = styled(Box)<{ component?: string }>({
  padding: '16px 16px 8px',
  background: '#ffffff',
  flexShrink: 0,
  borderTop: '1px solid #f1f5f9',
  boxShadow: '0 -4px 10px -2px rgba(0,0,0,0.02)',
});

const TextareaWrap = styled(Box)({
  position: 'relative',
});

const ChatTextarea = styled('textarea', {
  shouldForwardProp: p => p !== '$muted',
})<{ $muted: boolean }>(({ $muted }) => ({
  width: '100%',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '12px 40px 12px 12px',
  fontSize: '0.75rem',
  lineHeight: 1.5,
  resize: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  color: '#111827',
  background: '#ffffff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  transition: 'border-color 150ms, box-shadow 150ms',
  opacity: $muted ? 0.6 : 1,
  boxSizing: 'border-box',
  '&:focus': {
    borderColor: '#93c5fd',
    boxShadow: '0 0 0 3px rgba(147,197,253,0.3)',
  },
  '&::placeholder': { color: '#9ca3af' },
}));

const SendBtn = styled(IconButton)({
  position: 'absolute',
  right: 10,
  bottom: 10,
  width: 28,
  height: 28,
  color: '#d1d5db',
  '&:hover': { color: '#3b82f6', background: 'transparent' },
  '&:disabled': { color: '#e5e7eb' },
});
